import React, { useState, useEffect, useCallback } from 'react';
import './FieldMappingPage.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import { useCSVStore } from '../stores/csvStore';
import useFinancialRecordStore from '../store/financialRecordStore';
import useAuthStore from '../store/authStore';
import financialRecordKPIService from '../services/financialRecordKPIService';
import kpiEnrichmentService from '../services/kpiEnrichmentService';

// New KPI-based imports
import { 
  processKPIBasedCSV, 
  processKPIBasedCSVWithDatabase,
  detectColumnMapping, 
  validateColumnMapping,
  CSVColumnMapping,
} from '../utils/csvProcessor';
import { CSVProcessingResult } from '../types/data/csvProcessingResult';
import { FinancialRecord, FinancialRecordInput } from '../types/data/financialRecord';
import { groupFinancialInputs, convertInputsToRecordsWithDatabase } from '../utils/kpiUtils';
import DocumentationUpload from '../components/kpi/DocumentationUpload';

// API base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const FieldMappingPage: React.FC = () => {
  const navigate = useNavigate();
  const { uploadedFile, csvData, selectedLocation, accessibleLocations, setAccessibleLocations, isMultiLocationMode } = useCSVStore();
  const { createRecord } = useFinancialRecordStore();
  const { user } = useAuthStore();

  const [csvFile, setCSVFile] = useState<any>(null);
  const [processingData, setProcessingData] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [processingMessage, setProcessingMessage] = useState('Preparing to process data...');
  
  // New KPI-based CSV processing state
  const [columnMapping, setColumnMapping] = useState<Partial<CSVColumnMapping>>({});
  const [processedData, setProcessedData] = useState<CSVProcessingResult | null>(null);
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  
  // New preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewRecords, setPreviewRecords] = useState<FinancialRecord[]>([]);
  const [savingRecords, setSavingRecords] = useState(false);
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [saveResult, setSaveResult] = useState<{ saved: number; errors: number; duplicates: number } | null>(null);
  const [duplicateDetails, setDuplicateDetails] = useState<Array<{ record: any; existing: any }>>([]);
  const [errorDetails, setErrorDetails] = useState<Array<{ record: any; error: string }>>([]);
  const [expandedKPIs, setExpandedKPIs] = useState<Set<number>>(new Set());
  
  // New state for record creation
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [creationSuccess, setCreationSuccess] = useState<{ successCount: number; errorCount: number; duplicateCount: number } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  
  // Location state for dealer name injection
  // KPI enrichment state
  const [showBulkEnrichment, setShowBulkEnrichment] = useState(false);
  const [bulkEnrichmentData, setBulkEnrichmentData] = useState<Map<string, {
    code: string;
    instances: Array<{ recordIndex: number; kpiIndex: number; value: number }>;
    name: string;
    category: string;
    subcategory: string;
    unit: string;
  }>>(new Map());

  // Initialize with CSV file from store
  useEffect(() => {
    if (uploadedFile && (!csvFile || csvFile.id !== uploadedFile.id)) {
                              setCSVFile(uploadedFile);
    }
  }, [uploadedFile, csvFile]);

  // Fetch accessible locations if not already loaded in store
  useEffect(() => {
    if (accessibleLocations.length === 0) {
      fetchAccessibleLocations();
    }
  }, [accessibleLocations.length]);

  const fetchAccessibleLocations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch accessible locations: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAccessibleLocations(data.data);
      }
    } catch (error) {
      console.error('Error fetching accessible locations:', error);
      setAccessibleLocations([]);
    }
  };

  // Auto-detect column mapping when CSV data is available
  useEffect(() => {
    if (csvFile && csvFile.columns && csvFile.columns.length > 0) {
      const detectedMapping = detectColumnMapping(csvFile.columns);
      setColumnMapping(detectedMapping);
      
      // Check if we can auto-detect all required columns
      const validation = validateColumnMapping(detectedMapping);
      if (validation.valid) {
        setShowColumnMapping(false);
      } else {
        setShowColumnMapping(true);
      }
    }
  }, [csvFile]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoBack = () => {
    navigate('/upload-csv');
  };

  // Modified to only process CSV, not save records
  const handleContinue = async () => {
    // Validate column mapping first
    const mappingValidation = validateColumnMapping(columnMapping);
    if (!mappingValidation.valid) {
      setProcessingMessage(`Missing required columns: ${mappingValidation.missingColumns.join(', ')}`);
      setShowColumnMapping(true);
      return;
    }

    if (!csvFile || !csvFile.rows || csvFile.rows.length === 0) {
      setProcessingMessage('No CSV data available to process');
      return;
    }

    setProcessingData(true);
    setProcessingMessage('Processing CSV data with KPI-based format...');
    setProcessingProgress({ current: 0, total: csvFile.rows.length });

    try {
      // Debug logging
                              // Check if KPI metadata columns are mapped
      const hasKpiNameColumn = columnMapping.kpiName !== undefined;
      const hasKpiCategoryColumn = columnMapping.category !== undefined;
      const hasKpiSubcategoryColumn = columnMapping.subcategory !== undefined;
      const hasKpiUnitsColumn = columnMapping.units !== undefined;
      
      // Log if we found KPI metadata columns
      if (hasKpiNameColumn || hasKpiCategoryColumn || hasKpiSubcategoryColumn || hasKpiUnitsColumn) {
                setProcessingMessage('Found KPI metadata columns! Enrichment will be applied automatically.');
      }
      
      // Reconstruct full CSV data array (headers + data rows) for the processor
      const fullCSVData = [csvFile.columns, ...csvFile.rows];
      
      // Process CSV data using the new KPI-based processor with database enrichment
      const result = await processKPIBasedCSVWithDatabase(fullCSVData, columnMapping as CSVColumnMapping);
      setProcessedData(result);

            if (result.errors.length > 0) {
        console.error('DEBUG FIELD MAPPING: Processing had errors:', result.errors);
        setProcessingMessage(`Processing had errors: ${result.errors.join('; ')}`);
      }

      if (result.inputs.length === 0) {
        console.warn('DEBUG FIELD MAPPING: No valid inputs found');
        setProcessingMessage('No valid inputs found in CSV data');
        setProcessingData(false);
        return;
      }

                  // Convert inputs to records for preview
      const groupedInputs = groupFinancialInputs(result.inputs);
      const records = await convertInputsToRecordsWithDatabase(groupedInputs);

      // Calculate KPI enrichment stats after processing
      const fullyEnrichedCount = records.reduce((count, record) => {
        return count + record.kpis.filter(kpi => 
          kpi.name && kpi.name !== `KPI ${kpi.code}` && 
          kpi.category && kpi.category !== 'Other' &&
          kpi.unit && kpi.unit !== 'unknown'
        ).length;
      }, 0);
      
      const totalKPIs = records.reduce((count, record) => count + record.kpis.length, 0);
      const enrichmentPercentage = Math.round((fullyEnrichedCount / totalKPIs) * 100);
      
      // Update processing message with enrichment stats
      if (hasKpiNameColumn && hasKpiCategoryColumn && hasKpiUnitsColumn) {
        setProcessingMessage(`Successfully processed ${records.length} records with ${enrichmentPercentage}% of KPIs auto-enriched from CSV data.`);
      } else {
        setProcessingMessage(`Successfully processed ${records.length} records. ${enrichmentPercentage}% of KPIs have complete metadata.`);
      }
      
      // Set preview records and show preview
      setPreviewRecords(records);
      setShowPreview(true);
      setProcessingData(false);

    } catch (error) {
      console.error('Error processing CSV:', error);
      console.error('DEBUG FIELD MAPPING: Full error details:', error);
      setProcessingMessage(`Processing error: ${error instanceof Error ? error.message : String(error)}`);
      setProcessingData(false);
    }
  };

  // Check for potential duplicates before saving
  const checkForPotentialDuplicates = async () => {
    try {
      // Get existing records to check for duplicates
      const existingRecords = await financialRecordKPIService.getFinancialRecords();
      const existingKeys = new Set(
        existingRecords.records.map(record => 
          `${record.brand}-${record.dealer}-${record.year}-${record.month}`
        )
      );
      
      const potentialDuplicates = previewRecords.filter(record =>
        existingKeys.has(`${record.brand}-${record.dealer}-${record.year}-${record.month}`)
      );
      
      return potentialDuplicates;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return [];
    }
  };

  // Extract unique dealers and KPIs from preview records
  const extractUniqueDataFromRecords = (records: FinancialRecord[]) => {
    const uniqueDealers = new Set<string>();
    const uniqueKPIs = new Set<string>();
    
    records.forEach(record => {
      // Extract dealer names
      if (record.dealer && record.dealer.trim()) {
        uniqueDealers.add(record.dealer.trim());
      }
      
      // Extract KPI codes from each record's KPIs array
      record.kpis.forEach(kpi => {
        if (kpi.code && kpi.code.trim()) {
          uniqueKPIs.add(kpi.code.trim());
        }
      });
    });
    
    return {
      dealers: Array.from(uniqueDealers),
      kpis: Array.from(uniqueKPIs)
    };
  };

  // Get existing unique data for the current user
  const getExistingUniqueData = async () => {
    try {
      const [dealersResponse, kpisResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/unique-data/dealers`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        fetch(`${API_BASE_URL}/unique-data/kpis`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
      ]);
      
      if (!dealersResponse.ok || !kpisResponse.ok) {
        throw new Error('Failed to fetch existing unique data');
      }
      
      const existingDealers = await dealersResponse.json();
      const existingKPIs = await kpisResponse.json();
      
      return {
        dealers: new Set(existingDealers.dealers?.map((d: any) => d.dealerName) || []),
        kpis: new Set(existingKPIs.kpis?.map((k: any) => k.kpiName) || [])
      };
    } catch (error) {
      console.error('Error fetching existing unique data:', error);
      // Return empty sets if fetch fails - will attempt to store all items
      return {
        dealers: new Set<string>(),
        kpis: new Set<string>()
      };
    }
  };

  // Store only new unique data for the user
  const storeOnlyNewUniqueData = async (allDealers: string[], allKPIs: string[]) => {
    try {
      // Get existing data first
      const existing = await getExistingUniqueData();
      
      // Filter to only new items
      const newDealers = allDealers.filter(dealer => !existing.dealers.has(dealer));
      const newKPIs = allKPIs.filter(kpi => !existing.kpis.has(kpi));
      
      console.log(`Unique data analysis: ${newDealers.length} new dealers, ${newKPIs.length} new KPIs out of ${allDealers.length} dealers, ${allKPIs.length} KPIs total`);
      
      // Only store new items if any exist
      if (newDealers.length === 0 && newKPIs.length === 0) {
        return { success: true, dealersStored: 0, kpisStored: 0, skipped: true };
      }
      
      const promises = [];
      
      // Store new dealers if any found
      if (newDealers.length > 0) {
        promises.push(
          fetch(`${API_BASE_URL}/unique-data/dealers/bulk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ dealerNames: newDealers })
          })
        );
      }
      
      // Store new KPIs if any found
      if (newKPIs.length > 0) {
        promises.push(
          fetch(`${API_BASE_URL}/unique-data/kpis/bulk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ kpiNames: newKPIs })
          })
        );
      }
      
      // Execute all requests in parallel
      const responses = await Promise.all(promises);
      
      // Check for any failures
      const failedRequests = responses.filter(response => !response.ok);
      if (failedRequests.length > 0) {
        console.warn('Some unique data storage requests failed:', failedRequests);
        return { success: false, error: 'Some storage requests failed' };
      }
      
      return { success: true, dealersStored: newDealers.length, kpisStored: newKPIs.length, skipped: false };
    } catch (error) {
      console.error('Error storing unique data:', error);
      return { success: false, error };
    }
  };

  // New function to actually save records to database with enriched KPIs
  const handleCreateRecords = async () => {
    if (!processedData) return;
    
    setIsCreating(true);
    setCreationError(null);
    
    try {
      // Group and convert inputs to records
      const groupedInputs = groupFinancialInputs(processedData.inputs);
      const records = await convertInputsToRecordsWithDatabase(groupedInputs);
      
          // Add location ID to all records - handle both single and multi-location modes
    const recordsWithLocation = records.map(record => {
      let locationId: number | undefined;
      
      if (isMultiLocationMode) {
        // Multi-location mode: map dealer name to location ID
        const dealerName = record.dealer?.trim();
        if (dealerName) {
          const location = accessibleLocations.find(loc => 
            loc.name.toLowerCase().trim() === dealerName.toLowerCase().trim()
          );
          locationId = location?.id;
          console.log(`🔍 DEBUG MULTI-LOCATION MAPPING: Dealer "${dealerName}" -> Location ID ${locationId}`);
        }
      } else {
        // Single location mode: use selected location
        locationId = selectedLocation?.id;
      }
      
      return {
        ...record,
        location_id: locationId
      };
    });

    console.log('🔍 DEBUG RECORD CREATION: Multi-location mode:', isMultiLocationMode);
    console.log('🔍 DEBUG RECORD CREATION: Selected location:', selectedLocation);
    console.log('🔍 DEBUG RECORD CREATION: Accessible locations count:', accessibleLocations.length);
    console.log('🔍 DEBUG RECORD CREATION: Sample record with location:', recordsWithLocation[0]);
      
      // Create records
      const result = await financialRecordKPIService.bulkCreateFinancialRecords(recordsWithLocation);
      
      console.log('Records created:', result);
      
      // Show success message
      setCreationSuccess({
        successCount: result.success.length,
        errorCount: result.errors.length,
        duplicateCount: result.duplicates.length
      });
      
      // Clear processed data
      setProcessedData(null);
      setColumnMapping({});
      setCSVFile(null);
      setFile(null);
      setFileName('');
      
      // Navigate to financial data page after a delay
      setTimeout(() => {
        navigate('/financial-data');
      }, 3000);
      
    } catch (error) {
      console.error('Error creating records:', error);
      setCreationError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsCreating(false);
    }
  };

  // Column mapping handlers
  const handleColumnMappingChange = (field: keyof CSVColumnMapping, columnIndex: number | undefined) => {
    setColumnMapping(prev => ({
      ...prev,
      [field]: columnIndex
    }));
  };

  const handleColumnMappingComplete = () => {
    const validation = validateColumnMapping(columnMapping);
    if (validation.valid) {
      setShowColumnMapping(false);
    } else {
      setProcessingMessage(`Missing required columns: ${validation.missingColumns.join(', ')}`);
    }
  };

  // Toggle KPI dropdown for a specific record
  const toggleKPIExpansion = (recordIndex: number) => {
    setExpandedKPIs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordIndex)) {
        newSet.delete(recordIndex);
      } else {
        newSet.add(recordIndex);
      }
      return newSet;
    });
  };

  // Check if all records have complete KPI data
  const validateRecordsCompleteness = () => {
    return previewRecords.every(record => 
      record.kpis.every(kpi => {
        const hasRealName = kpi.name && kpi.name !== `KPI ${kpi.code}`;
        const hasRealCategory = kpi.category && kpi.category !== 'Other';
        // Consider 'unknown' as a valid unit value since we set it as default
        const hasRealUnit = kpi.unit && kpi.unit !== '';
        // Consider 'Unknown' as a valid subcategory since we set it as default
        const hasSubcategory = !!kpi.subcategory;
        return hasRealName && hasRealCategory && hasRealUnit && hasSubcategory;
      })
    );
  };

  // Get incomplete records count
  const getIncompleteRecordsInfo = () => {
    let incompleteRecords = 0;
    let incompleteKPIs = 0;
    
    previewRecords.forEach(record => {
      const hasIncompleteKPIs = record.kpis.some(kpi => {
        const hasRealName = kpi.name && kpi.name !== `KPI ${kpi.code}`;
        const hasRealCategory = kpi.category && kpi.category !== 'Other';
        // Consider 'unknown' as a valid unit value since we set it as default
        const hasRealUnit = kpi.unit && kpi.unit !== '';
        // Consider 'Unknown' as a valid subcategory since we set it as default
        const hasSubcategory = !!kpi.subcategory;
        return !(hasRealName && hasRealCategory && hasRealUnit && hasSubcategory);
      });
      if (hasIncompleteKPIs) {
        incompleteRecords++;
        incompleteKPIs += record.kpis.filter(kpi => {
          const hasRealName = kpi.name && kpi.name !== `KPI ${kpi.code}`;
          const hasRealCategory = kpi.category && kpi.category !== 'Other';
          // Consider 'unknown' as a valid unit value since we set it as default
          const hasRealUnit = kpi.unit && kpi.unit !== '';
          // Consider 'Unknown' as a valid subcategory since we set it as default
          const hasSubcategory = !!kpi.subcategory;
          return !(hasRealName && hasRealCategory && hasRealUnit && hasSubcategory);
        }).length;
      }
    });
    
    return { incompleteRecords, incompleteKPIs };
  };

  // Start bulk KPI enrichment
  const startBulkEnrichment = () => {
    const kpiCodeGroups = new Map<string, {
      code: string;
      instances: Array<{ recordIndex: number; kpiIndex: number; value: number }>;
      name: string;
      category: string;
      subcategory: string;
      unit: string;
    }>();

    previewRecords.forEach((record, recordIndex) => {
      record.kpis.forEach((kpi, kpiIndex) => {
        const hasRealName = kpi.name && kpi.name !== `KPI ${kpi.code}`;
        const hasRealCategory = kpi.category && kpi.category !== 'Other';
        // Consider 'unknown' as a valid unit value since we set it as default
        const hasRealUnit = kpi.unit && kpi.unit !== '';
        // Consider 'Unknown' as a valid subcategory since we set it as default
        const hasSubcategory = !!kpi.subcategory;
        
        const isIncomplete = !(hasRealName && hasRealCategory && hasRealUnit && hasSubcategory);
        
        if (isIncomplete) {
          // Group by KPI code instead of individual instances
          if (!kpiCodeGroups.has(kpi.code)) {
            kpiCodeGroups.set(kpi.code, {
              code: kpi.code,
              instances: [],
              name: hasRealName ? (kpi.name || '') : '',
              category: hasRealCategory ? (kpi.category || '') : '',
              subcategory: kpi.subcategory || '',
              unit: hasRealUnit ? (kpi.unit || '') : ''
            });
          }
          
          // Add this instance to the group
          kpiCodeGroups.get(kpi.code)!.instances.push({
            recordIndex,
            kpiIndex,
            value: kpi.value
          });
        }
      });
    });

    setBulkEnrichmentData(kpiCodeGroups);
    setShowBulkEnrichment(true);
  };

  // Update bulk enrichment data
  const updateBulkEnrichmentData = (key: string, field: string, value: string) => {
    setBulkEnrichmentData(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(key);
      if (item) {
        newMap.set(key, { ...item, [field]: value });
      }
      return newMap;
    });
  };

  // Save individual KPI enrichment
  const saveIndividualKPI = async (key: string) => {
    const enrichmentItem = bulkEnrichmentData.get(key);
    if (!enrichmentItem) return;

    // Check if this KPI is complete
    if (!enrichmentItem.name || !enrichmentItem.category || !enrichmentItem.unit) {
      alert('Please fill in all required fields (Name, Category, Units) before saving.');
      return;
    }

    try {
      const enrichment = {
        kpiCode: enrichmentItem.code,
        name: enrichmentItem.name,
        category: enrichmentItem.category,
        subcategory: enrichmentItem.subcategory || undefined,
        units: enrichmentItem.unit,
        description: `${enrichmentItem.name}${enrichmentItem.subcategory ? ` - ${enrichmentItem.subcategory}` : ''}`
      };

      // Save to database via service
      const result = await kpiEnrichmentService.saveEnrichment(enrichment);
      
      if (!result) {
        throw new Error('Failed to save KPI enrichment to database');
      }
      
      console.log('Individual KPI enrichment saved:', result);

      // Update ALL instances of this KPI code in local records
      const updatedRecords = [...previewRecords];
      const { instances, name, category, subcategory, unit } = enrichmentItem;
      
      instances.forEach(({ recordIndex, kpiIndex }) => {
        const kpi = updatedRecords[recordIndex].kpis[kpiIndex];
        kpi.name = name || kpi.code;
        kpi.category = category || 'Other';
        kpi.subcategory = subcategory;
        kpi.unit = unit || 'unknown';
      });
      
      setPreviewRecords(updatedRecords);

      // Remove this KPI code from bulk enrichment data since it's saved
      const newBulkData = new Map(bulkEnrichmentData);
      newBulkData.delete(key);
      setBulkEnrichmentData(newBulkData);

      console.log(`Successfully enriched and saved KPI: ${enrichment.kpiCode} (${instances.length} instances)`);
    } catch (error) {
      console.error('Error saving individual KPI enrichment:', error);
      alert('Failed to save KPI enrichment to database. Please try again.');
    }
  };

  // Save all completed KPIs
  const saveBulkEnrichment = async (onlyCompleted: boolean = false) => {
    try {
                        // Filter enrichments based on completion status
      const enrichments = Array.from(bulkEnrichmentData.values())
        .filter(item => {
          const isComplete = item.name && item.category && item.unit;
          return onlyCompleted ? isComplete : true;
        })
        .map(item => ({
          kpiCode: item.code,
          name: item.name || item.code,
          category: item.category || 'Other',
          subcategory: item.subcategory || undefined,
          units: item.unit || 'unknown',
          description: `${item.name || item.code}${item.subcategory ? ` - ${item.subcategory}` : ''}`
        }));

                  if (enrichments.length === 0) {
        console.warn('DEBUG BULK ENRICHMENT: No enrichments to save');
        alert(onlyCompleted ? 'No completed KPIs to save.' : 'No KPIs to save.');
        return;
      }

      // Save to database via service
            const result = await kpiEnrichmentService.bulkSaveEnrichments(enrichments);
      
            if (result.errors.length > 0) {
        console.warn('Some KPI enrichments failed to save:', result.errors);
      }
      
      console.log(`KPI enrichments saved: ${result.created} created, ${result.updated} updated`);

      // Update local records for immediate display
      const updatedRecords = [...previewRecords];
      const keysToRemove: string[] = [];
      
      bulkEnrichmentData.forEach((enrichmentItem, key) => {
        const isComplete = enrichmentItem.name && enrichmentItem.category && enrichmentItem.unit;
        if (!onlyCompleted || isComplete) {
          const { instances, name, category, subcategory, unit } = enrichmentItem;
          
          // Update ALL instances of this KPI code
          instances.forEach(({ recordIndex, kpiIndex }) => {
            const kpi = updatedRecords[recordIndex].kpis[kpiIndex];
            kpi.name = name || kpi.code;
            kpi.category = category || 'Other';
            kpi.subcategory = subcategory;
            kpi.unit = unit || 'unknown';
          });

          if (isComplete) {
            keysToRemove.push(key);
          }
        }
      });
      
      setPreviewRecords(updatedRecords);

      // Remove saved KPIs from bulk enrichment data
      if (onlyCompleted) {
        const newBulkData = new Map(bulkEnrichmentData);
        keysToRemove.forEach(key => newBulkData.delete(key));
        setBulkEnrichmentData(newBulkData);
        
        if (newBulkData.size === 0) {
          setShowBulkEnrichment(false);
        }
      } else {
        setShowBulkEnrichment(false);
        setBulkEnrichmentData(new Map());
      }

      // Show success message
      console.log(`Successfully enriched ${enrichments.length} KPIs and saved to learning database`);
    } catch (error) {
      console.error('Error saving KPI enrichments:', error);
      alert('Failed to save KPI enrichments to database. Please try again.');
    }
  };

  // Cancel bulk enrichment
  const cancelBulkEnrichment = () => {
    setShowBulkEnrichment(false);
    setBulkEnrichmentData(new Map());
  };

  // Check if bulk enrichment is complete
  const isBulkEnrichmentComplete = () => {
    return Array.from(bulkEnrichmentData.values()).every(item => 
      item.name && item.category && item.subcategory && item.unit
    );
  };

  // AI enrichment state
  const [showAIEnrichment, setShowAIEnrichment] = useState(false);
  const [aiEnrichmentFiles, setAIEnrichmentFiles] = useState<File[]>([]);
  const [isAIEnriching, setIsAIEnriching] = useState(false);
  const [aiEnrichmentProgress, setAIEnrichmentProgress] = useState('');

  // Add new state for showing manual enrichment button
  const [showManualEnrichmentButton, setShowManualEnrichmentButton] = useState(false);

  // Start AI enrichment with documentation
  const startAIEnrichment = () => {
    // Populate bulk enrichment data for AI enrichment (same logic as startBulkEnrichment)
    const kpiCodeGroups = new Map<string, {
      code: string;
      instances: Array<{ recordIndex: number; kpiIndex: number; value: number }>;
      name: string;
      category: string;
      subcategory: string;
      unit: string;
    }>();

    previewRecords.forEach((record, recordIndex) => {
      record.kpis.forEach((kpi, kpiIndex) => {
        const hasRealName = kpi.name && kpi.name !== `KPI ${kpi.code}`;
        const hasRealCategory = kpi.category && kpi.category !== 'Other';
        const hasRealUnit = kpi.unit && kpi.unit !== 'unknown';
        const hasSubcategory = kpi.subcategory;
        
        const isIncomplete = !(hasRealName && hasRealCategory && hasRealUnit && hasSubcategory);
        
        if (isIncomplete) {
          // Group by KPI code instead of individual instances
          if (!kpiCodeGroups.has(kpi.code)) {
            kpiCodeGroups.set(kpi.code, {
              code: kpi.code,
              instances: [],
              name: hasRealName ? (kpi.name || '') : '',
              category: hasRealCategory ? (kpi.category || '') : '',
              subcategory: kpi.subcategory || '',
              unit: hasRealUnit ? (kpi.unit || '') : ''
            });
          }
          
          // Add this instance to the group
          kpiCodeGroups.get(kpi.code)!.instances.push({
            recordIndex,
            kpiIndex,
            value: kpi.value
          });
        }
      });
    });

    setBulkEnrichmentData(kpiCodeGroups);
    setShowAIEnrichment(true);
    setAIEnrichmentFiles([]);
  };

  // Handle AI enrichment with uploaded documentation
  const performAIEnrichment = async (saveToDatabase: boolean = false) => {
    if (bulkEnrichmentData.size === 0) {
      alert('No KPIs available for enrichment');
      return;
    }

    setIsAIEnriching(true);
    setAIEnrichmentProgress('Preparing AI enrichment...');

    try {
      // Prepare optimized data for AI enrichment - only unique KPI codes and minimal context
      const kpiCodes = Array.from(bulkEnrichmentData.keys());
      const totalKpis = kpiCodes.length;
      const BATCH_SIZE = 50;
      
      // Calculate the number of batches needed
      const batchCount = Math.ceil(totalKpis / BATCH_SIZE);
      
      // Create batches of KPI codes
      const batches: string[][] = [];
      for (let i = 0; i < batchCount; i++) {
        const start = i * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, totalKpis);
        batches.push(kpiCodes.slice(start, end));
      }
      
      const csvContext = {
        brand: previewRecords[0]?.brand || 'Unknown',
        dealer: previewRecords[0]?.dealer || 'Unknown',
        headers: csvFile?.columns || [],
        // Only send a few sample KPI codes for context, not all data
        sampleData: kpiCodes.slice(0, 5).map(code => ({ kpiCode: code }))
      };

      setAIEnrichmentProgress(`Beginning sequential processing of ${totalKpis} KPIs in ${batchCount} batches...`);
      
      // Process batches sequentially
      const combinedEnrichments: any[] = [];
      let highestConfidence = 0;
      let totalEnriched = 0;
      
      // Process each batch sequentially
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batchKpis = batches[batchIndex];
        const currentBatchNumber = batchIndex + 1;
        
        // Update progress with current batch information
        setAIEnrichmentProgress(`Processing batch ${currentBatchNumber} of ${batchCount} (${batchKpis.length} KPIs)...`);
        
        try {
          // Prepare form data for this batch
          const formData = new FormData();
          formData.append('kpiCodes', JSON.stringify(batchKpis));
          formData.append('csvContext', JSON.stringify(csvContext));
          formData.append('saveToDatabase', saveToDatabase.toString());
          formData.append('modelId', 'arn:aws:bedrock:eu-central-1::inference-profile/eu.anthropic.claude-3-7-sonnet-20250219-v1:0'); // Claude 3.7 Sonnet Inference Profile
          
          // Add documentation files to each batch
          aiEnrichmentFiles.forEach((file) => {
            formData.append('documentation', file);
          });
          
          const response = await fetch(`${API_BASE_URL}/kpi-enrichments-ai/enrich-with-docs`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Batch ${currentBatchNumber} failed`);
          }
          
          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.message || `Batch ${currentBatchNumber} failed with error`);
          }
          
          // Add enrichments from this batch to the combined results
          combinedEnrichments.push(...result.data.enrichments);
          totalEnriched += result.data.enrichments.length;
          
          // Track the highest confidence score from all batches
          if (result.data.metadata && result.data.metadata.confidence > highestConfidence) {
            highestConfidence = result.data.metadata.confidence;
          }
          
          // Update progress showing completion of this batch and total progress
          const processedKPIs = batchKpis.length + (batchIndex > 0 ? batches.slice(0, batchIndex).flat().length : 0);
          const percentComplete = Math.round((processedKPIs / totalKpis) * 100);
          setAIEnrichmentProgress(`Completed batch ${currentBatchNumber}/${batchCount} (${percentComplete}% of KPIs processed)`);
          
        } catch (error) {
          console.error(`Error in batch ${currentBatchNumber}:`, error);
          throw new Error(`Failed during batch ${currentBatchNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      setAIEnrichmentProgress('Processing AI suggestions...');

      // Apply combined AI enrichments to both bulk enrichment data AND preview records
      const updatedBulkData = new Map(bulkEnrichmentData);
      const updatedRecords = [...previewRecords];

      combinedEnrichments.forEach((enrichment: any) => {
        // Update bulk enrichment data
        if (updatedBulkData.has(enrichment.kpiCode)) {
          const existingItem = updatedBulkData.get(enrichment.kpiCode)!;
          updatedBulkData.set(enrichment.kpiCode, {
            ...existingItem,
            name: enrichment.name || existingItem.name,
            category: enrichment.category || existingItem.category,
            subcategory: enrichment.subcategory || existingItem.subcategory,
            unit: enrichment.units || existingItem.unit
          });

          // Update preview records to reflect changes immediately
          existingItem.instances.forEach(({ recordIndex, kpiIndex }) => {
            const kpi = updatedRecords[recordIndex].kpis[kpiIndex];
            if (kpi.code === enrichment.kpiCode) {
              kpi.name = enrichment.name || kpi.name;
              kpi.category = enrichment.category || kpi.category;
              kpi.subcategory = enrichment.subcategory || kpi.subcategory;
              kpi.unit = enrichment.units || kpi.unit;
            }
          });
        }
      });

      setBulkEnrichmentData(updatedBulkData);
      setPreviewRecords(updatedRecords);

      // Show success message but keep AI modal open
      setAIEnrichmentProgress(`AI enrichment complete! ${totalEnriched} KPIs enriched across ${batchCount} batches with ${highestConfidence}% confidence.`);

    } catch (error) {
      console.error('AI enrichment error:', error);
      setAIEnrichmentProgress(`AI enrichment failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => {
        setAIEnrichmentProgress('');
      }, 5000);
    } finally {
      setIsAIEnriching(false);
    }
  };

  // Cancel AI enrichment
  const cancelAIEnrichment = () => {
    setShowAIEnrichment(false);
    setAIEnrichmentFiles([]);
    setAIEnrichmentProgress('');
  };

  // Clear all KPI enrichments from database (for testing)
  const clearAllKPIEnrichments = async () => {
    const confirmClear = window.confirm(
      '⚠️ Clear All KPI Enrichments\n\n' +
      'This will permanently delete ALL KPI enrichment data from the database.\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to continue?'
    );

    if (!confirmClear) return;

    try {
      console.log('Clearing all KPI enrichments...');
      
      const response = await fetch(`${API_BASE_URL}/kpi-enrichments/clear-all`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to clear KPI enrichments');
      }

      const result = await response.json();
      
      if (result.success) {
        // Reset local KPI data to incomplete state
        const resetRecords = previewRecords.map(record => ({
          ...record,
          kpis: record.kpis.map(kpi => ({
            ...kpi,
            name: `KPI ${kpi.code}`, // Reset to default incomplete name
            category: 'Other', // Reset to default incomplete category
            subcategory: '', // Clear subcategory
            unit: 'unknown' // Reset to default incomplete unit
          }))
        }));
        
        setPreviewRecords(resetRecords);
        
        // Clear any bulk enrichment data
        setBulkEnrichmentData(new Map());
        
        alert(`✅ Successfully cleared ${result.data.deletedCount} KPI enrichments from the database. UI has been refreshed.`);
        console.log('KPI enrichments cleared and UI updated:', result);
      } else {
        throw new Error(result.message || 'Failed to clear KPI enrichments');
      }

    } catch (error) {
      console.error('Error clearing KPI enrichments:', error);
      alert(`❌ Failed to clear KPI enrichments: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Update the goToManualEnrichment function to properly handle the transition
  const goToManualEnrichment = () => {
    // First close the AI enrichment modal
    setShowAIEnrichment(false);
    // Then open the bulk enrichment modal after a short delay
    setTimeout(() => {
      setShowBulkEnrichment(true);
    }, 100);
  };

  if (!csvFile) {
    return (
      <div className="field-mapping-page">
        <Sidebar />
        <div className="field-mapping-content">
          <div className="error-message">
            <h2>No CSV file uploaded</h2>
            <p>Please go back and upload a CSV file first.</p>
            <button onClick={handleGoBack} className="primary-button">
              Go Back to Upload
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="field-mapping-page">
      <Sidebar />
      <div className="field-mapping-content">
        <div className="field-mapping-header">
          <h1>Map CSV Data - KPI Format</h1>
          <p>Configure how your CSV columns map to the required KPI format:</p>
          <p><strong>Brand, Dealer, Year, Month, KPI Code, Value</strong> + optional metadata</p>
        </div>

        {/* CSV Preview */}
        <div className="csv-preview-section">
          <h3>CSV Preview</h3>
          <div className="csv-preview">
            <table>
              <thead>
                <tr>
                  {csvFile.columns.map((col: string, idx: number) => (
                    <th key={idx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvFile.rows.slice(0, Math.min(csvFile.rows.length, 10)).map((row: string[], rowIdx: number) => (
                  <tr key={rowIdx}>
                    {row.map((cell: string, cellIdx: number) => (
                      <td key={cellIdx}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Column Mapping Interface */}
        {showColumnMapping && (
          <div className="column-mapping-section">
            <h3>Column Mapping</h3>
            <p>Please map your CSV columns to the required fields:</p>
            
            <div className="mapping-grid">
              {/* Required Fields */}
              <div className="mapping-group">
                <h4>Required Fields</h4>
                
                <div className="mapping-field">
                  <label>Brand Column:</label>
                  <select 
                    value={columnMapping.brand ?? ''} 
                    onChange={(e) => handleColumnMappingChange('brand', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Dealer Column:</label>
                  <select 
                    value={columnMapping.dealer ?? ''} 
                    onChange={(e) => handleColumnMappingChange('dealer', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Year Column:</label>
                  <select 
                    value={columnMapping.year ?? ''} 
                    onChange={(e) => handleColumnMappingChange('year', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Month Column:</label>
                  <select 
                    value={columnMapping.month ?? ''} 
                    onChange={(e) => handleColumnMappingChange('month', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>KPI Code Column:</label>
                  <select 
                    value={columnMapping.kpiCode ?? ''} 
                    onChange={(e) => handleColumnMappingChange('kpiCode', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Value Column:</label>
                  <select 
                    value={columnMapping.value ?? ''} 
                    onChange={(e) => handleColumnMappingChange('value', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Optional Metadata Fields */}
              <div className="mapping-group">
                <h4>Optional Metadata Fields</h4>
                
                <div className="mapping-field">
                  <label>KPI Name Column:</label>
                  <select 
                    value={columnMapping.kpiName ?? ''} 
                    onChange={(e) => handleColumnMappingChange('kpiName', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Category Column:</label>
                  <select 
                    value={columnMapping.category ?? ''} 
                    onChange={(e) => handleColumnMappingChange('category', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Subcategory Column:</label>
                  <select 
                    value={columnMapping.subcategory ?? ''} 
                    onChange={(e) => handleColumnMappingChange('subcategory', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="mapping-field">
                  <label>Units Column:</label>
                  <select 
                    value={columnMapping.units ?? ''} 
                    onChange={(e) => handleColumnMappingChange('units', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Select column...</option>
                    {csvFile.columns.map((col: string, idx: number) => (
                      <option key={idx} value={idx}>{col}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button onClick={handleColumnMappingComplete} className="primary-button">
              Confirm Column Mapping
            </button>
          </div>
        )}

        {/* Processing Results */}
        {processedData && (
          <div className="processing-results">
            <h3>Processing Results</h3>
            <div className={`result-summary ${processedData.errors.length === 0 ? 'success' : 'error'}`}>
              <p><strong>Status:</strong> {processedData.errors.length === 0 ? 'Success' : 'Completed with Errors'}</p>
              <p><strong>Valid Inputs:</strong> {processedData.inputs.length}</p>
              <p><strong>Errors:</strong> {processedData.errors.length}</p>
              <p><strong>Warnings:</strong> {processedData.warnings.length}</p>
            </div>
            
            {processedData.errors.length > 0 && (
              <div className="result-errors">
                <h4>Errors:</h4>
                <ul>
                  {processedData.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {processedData.warnings.length > 0 && (
              <div className="result-warnings">
                <h4>Warnings:</h4>
                <ul>
                  {processedData.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Processing Progress */}
        {(processingData || savingRecords) && (
          <div className="processing-status">
            <h3>{savingRecords ? 'Saving Records...' : 'Processing Data...'}</h3>
            <p>{processingMessage}</p>
            {processingProgress.total > 0 && (
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(processingProgress.current / processingProgress.total) * 100}%` }}
                ></div>
              </div>
            )}
          </div>
        )}

        {/* Record Creation Success Status */}
        {creationSuccess && (
          <div className="creation-success-status success">
            <div className="success-message">
              <h3>🎉 Records Created Successfully!</h3>
              <p>Your financial records have been successfully created and saved to the database.</p>
              <div className="success-details">
                <div className="success-stat">
                  <strong>{creationSuccess.successCount}</strong> Records Created
                </div>
                {creationSuccess.errorCount > 0 && (
                  <div className="error-stat">
                    <strong>{creationSuccess.errorCount}</strong> Error{creationSuccess.errorCount !== 1 ? 's' : ''}
                  </div>
                )}
                {creationSuccess.duplicateCount > 0 && (
                  <div className="duplicate-stat">
                    <strong>{creationSuccess.duplicateCount}</strong> Duplicate{creationSuccess.duplicateCount !== 1 ? 's' : ''} Skipped
                  </div>
                )}
              </div>
              <div className="success-actions">
                <p className="redirect-message">
                  ⏳ Redirecting to Records page in a few seconds...
                </p>
                <button 
                  onClick={() => navigate('/financial-data')}
                  className="primary-button"
                >
                  View Records Now
                </button>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="secondary-button"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Creation Error Status */}
        {creationError && (
          <div className="creation-error-status error">
            <div className="error-message">
              <h3>❌ Error Creating Records</h3>
              <p>There was an error creating your records. Please try again.</p>
              <div className="error-details">
                <p><strong>Error:</strong> {creationError}</p>
              </div>
              <div className="error-actions">
                <button 
                  onClick={() => setCreationError(null)}
                  className="secondary-button"
                >
                  Try Again
                </button>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="secondary-button"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Results Status */}
        {(saveSuccessful || (saveResult && (saveResult.duplicates > 0 || saveResult.errors > 0))) && saveResult && (
          <div className={`save-success-status ${saveSuccessful ? 'success' : 'warning'}`}>
            <div className="success-message">
              <h3>{saveSuccessful ? '✅ Records Successfully Saved!' : '⚠️ Save Operation Completed'}</h3>
              <p>{processingMessage}</p>
              <div className="success-details">
                <div className="success-stat">
                  <strong>{saveResult.saved}</strong> Records Saved
                </div>
                {saveResult.errors > 0 && (
                  <div className="error-stat">
                    <strong>{saveResult.errors}</strong> Error{saveResult.errors !== 1 ? 's' : ''}
                  </div>
                )}
                {saveResult.duplicates > 0 && (
                  <div className="duplicate-stat">
                    <strong>{saveResult.duplicates}</strong> Duplicate{saveResult.duplicates !== 1 ? 's' : ''} Rejected
                  </div>
                )}
              </div>
              
              {/* Detailed Duplicate Information */}
              {duplicateDetails.length > 0 && (
                <div className="duplicate-details">
                  <h4>🔄 Duplicate Records Rejected:</h4>
                  <div className="duplicate-list">
                    {duplicateDetails.map((duplicate, index) => (
                      <div key={index} className="duplicate-item">
                        <strong>{duplicate.record.brand} - {duplicate.record.dealer}</strong>
                        <span className="duplicate-period">
                          {duplicate.record.year}/{duplicate.record.month}
                        </span>
                        <small>Already exists in database</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Detailed Error Information */}
              {errorDetails.length > 0 && (
                <div className="error-details">
                  <h4>❌ Validation Errors:</h4>
                  <div className="error-list">
                    {errorDetails.map((error, index) => (
                      <div key={index} className="error-item">
                        <strong>{error.record.brand} - {error.record.dealer}</strong>
                        <span className="error-message">{error.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview Records */}
        {showPreview && previewRecords.length > 0 && (
          <div className="preview-records">
            <h3>Preview Records ({previewRecords.length} records)</h3>
            <p>Review the records before saving to the database:</p>
            <div className="records-table-container">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Dealer</th>
                    <th>Location ID</th>
                    <th>Year</th>
                    <th>Month</th>
                    <th>KPIs</th>
                    {previewRecords[0]?.additionalData && <th>Additional Data</th>}
                  </tr>
                </thead>
                <tbody>
                  {previewRecords.slice(0, 10).map((record, idx) => (
                    <tr key={idx}>
                      <td>{record.brand}</td>
                      <td>{record.dealer}</td>
                      <td>{selectedLocation?.id || 'Not Set'}</td>
                      <td>{record.year}</td>
                      <td>{record.month}</td>
                      <td>
                        <div className="kpi-dropdown">
                          <button 
                            className="kpi-toggle-button"
                            onClick={() => toggleKPIExpansion(idx)}
                            type="button"
                          >
                            <div className="kpi-count-info">
                              <span className="kpi-count">
                                {record.kpis.length} KPI{record.kpis.length !== 1 ? 's' : ''}
                              </span>
                              <div className="kpi-mix">
                                {record.kpis.every(kpi => 
                                  kpi.name && 
                                  kpi.category && 
                                  kpi.category !== 'Other' && 
                                  kpi.unit && 
                                  kpi.unit !== '' && 
                                  kpi.subcategory
                                ) ? (
                                  <span className="all-complete">All Complete</span>
                                ) : (
                                  <>
                                    <span className="complete-count">
                                      {record.kpis.filter(kpi => 
                                        kpi.name && 
                                        kpi.category && 
                                        kpi.category !== 'Other' && 
                                        kpi.unit && 
                                        kpi.unit !== '' && 
                                        kpi.subcategory
                                      ).length}
                                    </span>
                                    <span className="minimal-count">
                                      {record.kpis.filter(kpi => 
                                        !(kpi.name && 
                                        kpi.category && 
                                        kpi.category !== 'Other' && 
                                        kpi.unit && 
                                        kpi.unit !== '' && 
                                        kpi.subcategory)
                                      ).length}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <span>
                              {expandedKPIs.has(idx) ? '▼' : '▶'}
                            </span>
                          </button>
                          
                          {expandedKPIs.has(idx) && (
                            <div className="kpi-details-dropdown">
                              {record.kpis.map((kpi, kpiIdx) => {
                                const isComplete = kpi.name && 
                                  kpi.category && 
                                  kpi.category !== 'Other' && 
                                  kpi.unit && 
                                  kpi.unit !== '' && 
                                  kpi.subcategory;
                                
                                return (
                                  <div 
                                    key={kpiIdx} 
                                    className={`kpi-detail-item ${isComplete ? 'complete' : 'incomplete'}`}
                                  >
                                    <div className="kpi-header">
                                      <strong>{kpi.code}</strong>
                                      <span className="kpi-value">{kpi.value}</span>
                                    </div>
                                    
                                    <div className="kpi-details">
                                      <small>
                                        <span className={kpi.name ? 'has-data' : 'missing-data'}>
                                          Name: {kpi.name || 'Not provided'}
                                        </span>
                                        <br />
                                        <span className={kpi.category ? 'has-data' : 'missing-data'}>
                                          Category: {kpi.category || 'Not provided'}
                                        </span>
                                        {' / '}
                                        <span className={kpi.subcategory ? 'has-data' : 'missing-data'}>
                                          {kpi.subcategory || 'Not provided'}
                                        </span>
                                        <br />
                                        <span className={kpi.unit ? 'has-data' : 'missing-data'}>
                                          Units: {kpi.unit || 'Not provided'}
                                        </span>
                                      </small>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      {record.additionalData && (
                        <td>
                          <div className="additional-data-container">
                            {record.additionalData.map((item, adIdx) => (
                              <div key={adIdx} className="additional-data-item">
                                <strong>{item.field}:</strong> {item.value}
                              </div>
                            ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewRecords.length > 10 && (
                <p className="table-note">Showing first 10 of {previewRecords.length} records</p>
              )}
            </div>
            
            {(() => {
              const isComplete = validateRecordsCompleteness();
              const { incompleteRecords, incompleteKPIs } = getIncompleteRecordsInfo();
              
              return (
                <div className="preview-validation">
                  {!isComplete && (
                    <div className="validation-warning">
                      <h4>⚠️ Data Enrichment Required</h4>
                      <p>
                        <strong>{incompleteKPIs} KPIs</strong> in <strong>{incompleteRecords} records</strong> need enrichment before creation.
                      </p>
                      <div className="enrichment-buttons">
                        <button 
                          onClick={startBulkEnrichment}
                          className="enrich-all-button"
                          type="button"
                        >
                          📝 Enrich Manually
                        </button>
                        <button 
                          onClick={startAIEnrichment}
                          className="enrich-ai-button"
                          type="button"
                        >
                          🤖 AI Enrich with Docs
                        </button>
                        {user?.role === 'admin' && (
                          <button 
                            onClick={clearAllKPIEnrichments}
                            className="clear-enrichments-button"
                            type="button"
                            title="Clear all KPI enrichment data from database (for testing)"
                          >
                            🗑️ Clear All Enrichments
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {isComplete && (
                    <div className="validation-success">
                      <h4>✅ All Data Complete</h4>
                      <p>All records have complete KPI metadata and are ready for creation.</p>
                      <div className="success-actions">
                        {user?.role === 'admin' && (
                          <button 
                            onClick={clearAllKPIEnrichments}
                            className="clear-enrichments-button"
                            type="button"
                            title="Clear all KPI enrichment data from database (for testing)"
                          >
                            🗑️ Clear All Enrichments
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="preview-actions">
                    <button 
                      onClick={handleCreateRecords} 
                      disabled={isCreating || !isComplete}
                      className={`primary-button ${(!isComplete || isCreating) ? 'disabled' : ''}`}
                      title={isCreating ? 'Creating records, please wait...' : !isComplete ? 'Complete all KPI enrichment before creating records' : ''}
                    >
                      {isCreating ? 'Creating Records...' : `Create ${previewRecords.length} Records`}
                    </button>
                    <button 
                      onClick={() => setShowPreview(false)} 
                      disabled={isCreating}
                      className="secondary-button"
                    >
                      Back to Mapping
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Bulk KPI Enrichment Modal */}
        {showBulkEnrichment && (
          <div className="modal-overlay">
            <div className="bulk-enrichment-modal">
              <div className="modal-header">
                <h3>Enrich All KPIs ({bulkEnrichmentData.size} KPIs)</h3>
                <button 
                  className="modal-close"
                  onClick={cancelBulkEnrichment}
                  type="button"
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-body">
                <p className="enrichment-instructions">
                  Complete the missing information for all KPIs below. All fields are required.
                </p>
                
                <div className="bulk-enrichment-form">
                  {Array.from(bulkEnrichmentData.entries()).map(([key, item]) => (
                    <div key={key} className="kpi-enrichment-item">
                      <div className="kpi-info">
                        <strong>{item.code}</strong> ({item.instances.length} instance{item.instances.length > 1 ? 's' : ''})
                        <div className="instances-info">
                          {item.instances.map((instance, idx) => (
                            <span key={idx} className="instance-detail">
                              {previewRecords[instance.recordIndex].brand} - {previewRecords[instance.recordIndex].dealer}: {instance.value}
                              {idx < item.instances.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="enrichment-fields">
                        <div className="form-row">
                          <div className="form-group">
                            <label>KPI Name *</label>
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => updateBulkEnrichmentData(key, 'name', e.target.value)}
                              placeholder="e.g., Monthly Revenue"
                              required
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>Category *</label>
                            <input
                              type="text"
                              value={item.category}
                              onChange={(e) => updateBulkEnrichmentData(key, 'category', e.target.value)}
                              placeholder="e.g., Financial, Operational, Sales"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>Subcategory *</label>
                            <input
                              type="text"
                              value={item.subcategory}
                              onChange={(e) => updateBulkEnrichmentData(key, 'subcategory', e.target.value)}
                              placeholder="e.g., Revenue, Costs, Units"
                              required
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>Units *</label>
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => updateBulkEnrichmentData(key, 'unit', e.target.value)}
                              placeholder="e.g., USD, Units, Percentage, Hours"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="kpi-actions">
                          <button
                            onClick={() => saveIndividualKPI(key)}
                            disabled={!item.name || !item.category || !item.unit}
                            className={`save-individual-button ${(!item.name || !item.category || !item.unit) ? 'disabled' : ''}`}
                            type="button"
                            title={(!item.name || !item.category || !item.unit) ? 'Complete all required fields first' : 'Save this KPI'}
                          >
                            💾 Save This KPI
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  onClick={cancelBulkEnrichment}
                  className="secondary-button"
                  type="button"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => saveBulkEnrichment(true)}
                  className="secondary-button"
                  disabled={Array.from(bulkEnrichmentData.values()).every(item => !item.name || !item.category || !item.unit)}
                  type="button"
                  title="Save only the completed KPIs and keep the modal open for others"
                >
                  💾 Save Completed Only
                </button>
                <button 
                  onClick={() => saveBulkEnrichment(false)}
                  className="primary-button"
                  disabled={!isBulkEnrichmentComplete()}
                  type="button"
                  title="Save all KPIs (requires all to be completed)"
                >
                  💾 Save All & Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI KPI Enrichment Modal */}
        {showAIEnrichment && (
          <div className="modal-overlay">
            <div className="modal-content ai-enrichment-modal">
              <div className="modal-header">
                <h3>AI-Powered KPI Enrichment</h3>
                <button 
                  onClick={cancelAIEnrichment}
                  className="close-button"
                  type="button"
                  disabled={isAIEnriching}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="ai-enrichment-intro">
                  <p>Let AI automatically enrich your {bulkEnrichmentData.size} incomplete KPIs using advanced language models and optional documentation.</p>
                  <div className="ai-benefits">
                    <div className="benefit-item">⚡ <strong>Speed:</strong> Enrich hundreds of KPIs in seconds</div>
                    <div className="benefit-item">🎯 <strong>Accuracy:</strong> Context-aware suggestions using your data</div>
                    <div className="benefit-item">📚 <strong>Learning:</strong> Upload docs for better understanding</div>
                  </div>
                </div>

                <DocumentationUpload
                  onFilesSelected={setAIEnrichmentFiles}
                  maxFiles={5}
                  disabled={isAIEnriching}
                />

                {aiEnrichmentProgress && (
                  <div className={`ai-progress ${isAIEnriching ? 'loading' : ''}`}>
                    <div className="progress-text">{aiEnrichmentProgress}</div>
                    {isAIEnriching && (
                      <div className="progress-spinner">
                        <div className="spinner"></div>
                      </div>
                    )}
                  </div>
                )}

                <div className="ai-enrichment-options">
                  <div className="option-group">
                    <h4>AI Enrichment Options</h4>
                    <div className="option-item">
                      <strong>Model:</strong> Claude 3.7 Sonnet (Inference Profile)
                    </div>
                    <div className="option-item">
                      <strong>Context:</strong> {previewRecords[0]?.brand || 'Unknown'} - {previewRecords[0]?.dealer || 'Unknown'}
                    </div>
                    <div className="option-item">
                      <strong>KPIs to Enrich:</strong> {bulkEnrichmentData.size} incomplete KPIs
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  onClick={cancelAIEnrichment}
                  className="secondary-button"
                  disabled={isAIEnriching}
                  type="button"
                >
                  Cancel
                </button>
                {!isAIEnriching && !aiEnrichmentProgress.includes('complete') ? (
                  <button 
                    onClick={() => performAIEnrichment(false)}
                    className="primary-button"
                    disabled={isAIEnriching}
                    type="button"
                    title="Generate AI suggestions for manual review (you'll review and save them in the next step)"
                  >
                    🤖 Generate AI Suggestions
                  </button>
                ) : !isAIEnriching && aiEnrichmentProgress.includes('complete') ? (
                  <button 
                    onClick={goToManualEnrichment}
                    className="primary-button"
                    type="button"
                  >
                    📝 Review & Save Suggestions
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!showPreview && (
          <div className="field-mapping-actions">
            <button onClick={handleGoBack} className="secondary-button">
              Back to Upload
            </button>
            
            <button onClick={handleGoToDashboard} className="secondary-button">
              Go to Dashboard
            </button>
            
            <button 
              onClick={handleContinue} 
              disabled={processingData || showColumnMapping}
              className="primary-button"
            >
              {processingData ? 'Processing...' : 'Preview Records'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldMappingPage; 